/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { Button, Spacer, Table, Text } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { SidebarTextField } from "~/components/SidebarForm/inputs/SidebarTextField";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import FlexContainer from "~/components/Styled/FlexContainer";
import { showSuccess } from "~/components/Toast";
import {
  checkValue,
  checkVersion,
  generateActionSuccessText,
  useAxios,
} from "~/tools";
import { SelectService } from "./SelectService";
import { SelectedService } from "./types";

type ServiceGroupsAddProps = {
  refetch: () => void;
};
export type SelectedServiceType = {
  id: string;
  name: string;
  version: string;
};

function ServiceGroupsAdd({ refetch }: ServiceGroupsAddProps) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [selectedService, setSelectedService] = React.useState<
    Array<SelectedService>
  >([]);

  const methods = useForm();

  type Values = {
    name: string;
    version: string;
  };

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const onSubmit = async (values: Values) => {
    const flatServices = selectedService.map((s) => s.id);
    const organizeValues = {
      name: values.name,
      version: checkValue(values.version),
      serviceIds: flatServices,
    };
    setIsSidebarOpen(false);
    try {
      await Axios().post(`/catalog/serviceGroups`, organizeValues);
      refetch();
      showSuccess(generateActionSuccessText(`Service Group`)()(`added`)());
    } catch (e) {
      showError(e.response);
    } finally {
      clean();
    }
  };
  const clean = () => {
    setSelectedService([]);
  };

  const handleDelete = (id: string) => {
    setSelectedService((prevState: SelectedService[]) =>
      prevState.filter((service: SelectedService) => service?.id !== id)
    );
  };

  return (
    <>
      <Button
        onClick={() => setIsSidebarOpen(true)}
        css={{ marginLeft: "auto" }}
        icon="plus"
      >
        Add Service Group
      </Button>

      <SidebarForm
        title="Add Service Group"
        open={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        formMethods={methods}
        submit={onSubmit}
      >
        <SidebarTextField name="name" label="Name" required />
        <SidebarTextField
          name="version"
          label="Version"
          required
          registerOptions={{
            validate: checkVersion,
          }}
        />

        {selectedService.length !== 0 && (
          <>
            <Text color="warning" tag="div" size="sm">
              Service group cannot be edited after save.
            </Text>
            <Table small>
              <Table.thead>
                <Table.tr>
                  <Table.th css={{ width: "70%" }}>Service Name</Table.th>
                  <Table.th css={{ width: "25%", textAlign: "center" }}>
                    Version
                  </Table.th>
                  <Table.th css={{ width: "5%", textAlign: "center" }}>
                    Action
                  </Table.th>
                </Table.tr>
              </Table.thead>
              <Table.tbody>
                {selectedService?.map((r: SelectedServiceType) => (
                  <Table.tr key={r.id}>
                    <Table.td>{r.name}</Table.td>
                    <Table.td css={{ textAlign: "center" }}>
                      {r.version || "-"}
                    </Table.td>
                    <Table.td>
                      <FlexContainer justify="center">
                        <Button
                          onClick={() => handleDelete(r.id)}
                          type="button"
                          size="sm"
                          severity="low"
                          icon="minus"
                        />
                      </FlexContainer>
                    </Table.td>
                  </Table.tr>
                ))}
              </Table.tbody>
            </Table>
          </>
        )}
        <SelectService
          setSelectedService={setSelectedService}
          selectedServicesIds={selectedService?.map(
            (service: SelectedService) => service.id
          )}
        />
        <Spacer height="1rem" />

        <SidebarFormButtons
          submitEnabled={selectedService.length > 0}
          onCancel={() => {
            setSelectedService([]);
            setIsSidebarOpen(false);
          }}
        />
      </SidebarForm>
    </>
  );
}

export { ServiceGroupsAdd };
